// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/stay/ui.tsx
// 🔧 PURPOSE: I pezzi comuni di "Il mio soggiorno" nel nuovo stile (mockup
//             approvati "MsSoggiorno", "MsPrimaArrivo", "MsPartenza",
//             "MsSenzaAccesso"): l'occhiello colorato con il titolo in
//             Fraunces, la card avorio con il bordo tinto del suo colore,
//             la fascia a gradiente con l'icona nel vetro, le note col filo
//             a sinistra, il campo email, i bottoni maiuscoli.
//
//             I colori di sempre di ogni card restano; dove c'è testo
//             bianco piccolo il gradiente scende di un tono (600/700) per
//             stare sopra 4.5:1. Le fasce hanno solo il titolo grande
//             (Fraunces 19px grassetto): lì basta 3:1.
//             Accanto a ogni colore chiaro c'è il suo dark: (scala stone).
// ============================================

import React from 'react';
import { Mail, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { FOCUS_RING, INK_TEXT, MUTED_TEXT } from '../home/ui';
import { SERVICE_BUTTON, SERVICE_CARD } from '../../pages/services/ServicePageLayout';

export { FOCUS_RING, INK_TEXT, MUTED_TEXT, SERVICE_BUTTON, SERVICE_CARD };

// ---- Colori ---------------------------------------------------------------------

/** I gradienti dei bottoni (testo bianco 12.5px: ogni estremo oltre 4.5:1). */
export const STAY_BTN = {
  /** Porta, pagamenti, ricevute: indaco → viola (6.3 / 5.4:1). */
  indigo: 'bg-gradient-to-r from-indigo-600 to-purple-600 shadow-[0_8px_18px_rgba(79,70,229,.22)] dark:shadow-none',
  /** WiFi: indaco → blu (6.3 / 6.7:1). */
  wifi: 'bg-gradient-to-r from-indigo-600 to-blue-700 shadow-[0_8px_18px_rgba(29,78,216,.22)] dark:shadow-none',
  /** Check-in da fare: ambra → arancio un tono più scuri (5.0 / 5.2:1). */
  amber: 'bg-gradient-to-r from-amber-700 to-orange-700 shadow-[0_8px_18px_rgba(194,65,12,.22)] dark:shadow-none',
  /** Indicazioni: smeraldo → teal 700 (5.5:1). */
  emerald: 'bg-gradient-to-r from-emerald-700 to-teal-700 shadow-[0_8px_18px_rgba(4,120,87,.2)] dark:shadow-none',
  /** Check-out posticipato: indaco → viola scuro (6.3 / 7.1:1). */
  violet: 'bg-gradient-to-r from-indigo-600 to-violet-700 shadow-[0_8px_18px_rgba(79,70,229,.22)] dark:shadow-none',
  /** Clima acceso, "Chiudi" dopo un invio riuscito (5.5 / 5.0:1). */
  green: 'bg-gradient-to-r from-emerald-700 to-green-700 shadow-[0_8px_18px_rgba(4,120,87,.24)] dark:shadow-none',
  /** Volo cancellato: "Contattaci" (6.5 / 6.0:1). */
  red: 'bg-gradient-to-r from-red-700 to-rose-700 shadow-[0_8px_18px_rgba(190,18,60,.2)] dark:shadow-none',
  /** Accedi alla prenotazione: il cielo della testata, un tono più scuro (5.9 / 5.4:1). */
  sky: 'bg-gradient-to-br from-sky-700 to-cyan-700 shadow-[0_10px_22px_rgba(3,105,161,.25)] dark:shadow-none',
} as const;

/** Le fasce in cima alle card (solo il titolo grande: almeno 3:1). */
export const STAY_BAND = {
  indigo: 'bg-gradient-to-r from-indigo-600 to-purple-600',
  wifi: 'bg-gradient-to-r from-indigo-600 to-blue-700',
  amber: 'bg-gradient-to-r from-amber-600 to-orange-600',
  /** Con testo piccolo sopra (volo in ritardo): un tono più scuro. */
  amberDeep: 'bg-gradient-to-r from-amber-700 to-orange-700',
  sky: 'bg-gradient-to-r from-sky-600 to-cyan-600',
  emerald: 'bg-gradient-to-r from-emerald-600 to-teal-600',
  violet: 'bg-gradient-to-r from-indigo-600 to-violet-700',
} as const;

/** Il bordo chiaro di ogni famiglia di colore (in scuro: stone-800). */
export const STAY_TINT = {
  sky: 'border-[#DCE6F2]',
  teal: 'border-[#D3E9EE]',
  green: 'border-[#D6EADF]',
  lime: 'border-[#DDE9C9]',
  purple: 'border-[#E9DDF7]',
  violet: 'border-[#E0E1F7]',
  amber: 'border-[#F3E2C4]',
  red: 'border-[#F3CFCF]',
  neutral: 'border-nva-edge',
} as const;

export type StayTint = keyof typeof STAY_TINT;

/** Gli occhielli sul foglio avorio, nel tono 700/800 del colore della sezione. */
export const STAY_EYEBROW = {
  sky: 'text-sky-700 dark:text-sky-300',
  purple: 'text-purple-700 dark:text-purple-300',
  emerald: 'text-emerald-700 dark:text-emerald-300',
  amber: 'text-amber-800 dark:text-amber-300',
  gold: 'text-nva-gold-text dark:text-nva-gold-dark',
} as const;

// ---- Classi ---------------------------------------------------------------------

/** La card avorio (angoli 20px); il bordo tinto lo aggiunge chi la usa. */
export const STAY_CARD = SERVICE_CARD;
/** I riquadri crema dentro le card. */
export const CREAM_BOX = 'rounded-[14px] bg-nva-cream dark:bg-stone-800';
/** L'etichetta maiuscola piccola ("CHECK-IN", "RETE"). */
export const STAY_LABEL = 'text-[10.5px] font-extrabold uppercase leading-snug tracking-[0.14em] text-nva-muted dark:text-stone-400';
/** Il testo piccolo di aiuto. */
export const STAY_HINT = 'text-[12.5px] leading-[1.4] text-nva-muted dark:text-stone-400';
/** Il bottone a filo (secondario), stessa misura dei pieni. */
export const LINE_BUTTON = cn(
  SERVICE_BUTTON,
  'border border-nva-foot bg-nva-card text-nva-espresso dark:border-stone-600 dark:bg-stone-900 dark:text-stone-100',
);
/** Il quadratino da 44px accanto a un valore (copia, mostra, mappa). */
export const SQUARE_BUTTON = cn(
  'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-nva-edge bg-nva-card dark:border-stone-700 dark:bg-stone-900',
  INK_TEXT,
  FOCUS_RING,
);
/** Il link in grassetto ("Nascondi", "Mostra QR Code WiFi"); il colore lo aggiunge chi lo usa. */
export const TEXT_LINK = cn('inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-transparent px-2 text-[13.5px] font-bold', FOCUS_RING);

// ---- Titoli di sezione -----------------------------------------------------------

interface SectionHeadProps {
  eyebrow?: React.ReactNode;
  /** Il colore dell'occhiello (STAY_EYEBROW). */
  eyebrowClass?: string;
  title: React.ReactNode;
  /** L'id del titolo, per aria-labelledby. */
  id?: string;
  /** Accanto al titolo (il lucchetto di "Casa"). */
  titleAdornment?: React.ReactNode;
}

/** Occhiello colorato + titolo in Fraunces 23px, in minuscolo come le pagine nuove. */
export const SectionHead: React.FC<SectionHeadProps> = ({ eyebrow, eyebrowClass = STAY_EYEBROW.gold, title, id, titleAdornment }) => (
  <div className="flex flex-col gap-1">
    {eyebrow ? (
      <p className={cn('m-0 text-[11px] font-extrabold uppercase leading-snug tracking-[0.18em]', eyebrowClass)}>{eyebrow}</p>
    ) : null}
    <div className="flex items-center gap-2">
      <h2 id={id} className={cn('m-0 break-words font-display text-[23px] font-bold leading-[1.15]', INK_TEXT)}>{title}</h2>
      {titleAdornment}
    </div>
  </div>
);

// ---- La fascia a gradiente ---------------------------------------------------------

/** La X da 44px nel vetro, sulla fascia. */
export const GlassClose: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="ml-auto flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-white/20 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
  >
    <X aria-hidden="true" className="h-[18px] w-[18px]" strokeWidth={2.4} />
  </button>
);

/** La X da 44px sul crema (fuori dalle fasce). */
export const CreamClose: React.FC<{ onClick: () => void; label: string; className?: string }> = ({ onClick, label, className }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    data-sheet-close=""
    className={cn('flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-nva-cream dark:bg-stone-800', INK_TEXT, FOCUS_RING, className)}
  >
    <X aria-hidden="true" className="h-[18px] w-[18px]" strokeWidth={2.4} />
  </button>
);

interface BandProps {
  /** Il gradiente (STAY_BAND o quello della fase). */
  gradient: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  /** Il titolo come intestazione (h2 se la card apre una sezione, h3 dentro una sezione) o come testo. */
  titleAs?: 'h2' | 'h3' | 'span';
  titleId?: string;
  /** Il vetro dell'icona che pulsa (volo all'imbarco). */
  pulse?: boolean;
  /** A destra del titolo (la pillola di stato del volo). */
  trailing?: React.ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

/** La fascia colorata in cima alla card: il vetro con l'icona e il titolo bianco in Fraunces. */
export const Band: React.FC<BandProps> = ({ gradient, icon, title, titleAs = 'h3', titleId, pulse, trailing, onClose, closeLabel, className, children }) => {
  const Title = titleAs;
  return (
    <div className={cn('flex min-h-[60px] items-center gap-2.5 py-2.5 pl-3.5 pr-2.5', gradient, className)}>
      <span
        aria-hidden="true"
        className={cn(
          'flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-[10px] bg-white/20 text-white [&>svg]:h-[18px] [&>svg]:w-[18px]',
          pulse && 'motion-safe:animate-pulse',
        )}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Title id={titleId} className="m-0 break-words font-display text-[19px] font-bold leading-[1.15] text-white">
          {title}
        </Title>
        {children}
      </span>
      {trailing}
      {onClose ? <GlassClose onClick={onClose} label={closeLabel || ''} /> : null}
    </div>
  );
};

interface BandCardProps extends BandProps {
  tint: StayTint;
  /** L'ombra colorata leggera sotto la card (il colore del mockup). */
  shadow?: string;
  bodyClassName?: string;
  as?: 'div' | 'section';
  id?: string;
  role?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  body: React.ReactNode;
}

/** La card con la fascia: UN solo livello, la fascia fa parte della card. */
export const BandCard: React.FC<BandCardProps> = ({
  tint, shadow, bodyClassName, as = 'div', id, role, body, 'aria-label': ariaLabel, 'data-testid': testId, ...band
}) => {
  const Tag = as;
  return (
    <Tag
      id={id}
      role={role}
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(STAY_CARD, STAY_TINT[tint], 'overflow-hidden', shadow || 'shadow-[0_2px_12px_rgba(42,38,32,.05)]', 'dark:shadow-none')}
    >
      <Band {...band} />
      <div className={cn('flex flex-col gap-3 p-4', bodyClassName)}>{body}</div>
    </Tag>
  );
};

// ---- Note col filo ------------------------------------------------------------------

type NoteTone = 'amber' | 'sky' | 'gold' | 'stone' | 'emerald' | 'red';

const NOTE_TONE: Record<NoteTone, { box: string; ico: string; text: string }> = {
  amber: {
    box: 'border-amber-500 bg-amber-50 dark:border-amber-500 dark:bg-amber-950/40',
    ico: 'text-amber-700 dark:text-amber-300',
    text: 'text-amber-900 dark:text-amber-100',
  },
  sky: {
    box: 'border-sky-600 bg-sky-50 dark:border-sky-500 dark:bg-sky-950/40',
    ico: 'text-sky-700 dark:text-sky-300',
    text: 'text-sky-900 dark:text-sky-100',
  },
  gold: {
    box: 'border-nva-gold bg-nva-cream dark:bg-nva-cream-dark',
    ico: 'text-nva-gold-text dark:text-nva-gold-dark',
    text: INK_TEXT,
  },
  stone: {
    box: 'border-stone-400 bg-nva-cream dark:border-stone-500 dark:bg-stone-800',
    ico: 'text-stone-600 dark:text-stone-300',
    text: INK_TEXT,
  },
  emerald: {
    box: 'border-emerald-600 bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-950/40',
    ico: 'text-emerald-700 dark:text-emerald-300',
    text: 'text-emerald-900 dark:text-emerald-100',
  },
  red: {
    box: 'border-red-600 bg-red-50 dark:border-red-500 dark:bg-red-950/40',
    ico: 'text-red-700 dark:text-red-300',
    text: 'text-red-900 dark:text-red-100',
  },
};

interface RuleNoteProps {
  tone: NoteTone;
  icon: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  role?: string;
  className?: string;
}

/** La nota col filo di 3px a sinistra (angoli 4px / 14px). */
export const RuleNote: React.FC<RuleNoteProps> = ({ tone, icon, title, children, role, className }) => (
  <div
    role={role}
    className={cn('flex items-start gap-2.5 rounded-l-[4px] rounded-r-[14px] border-l-[3px] px-3.5 py-3', NOTE_TONE[tone].box, className)}
  >
    <span aria-hidden="true" className={cn('mt-px flex flex-shrink-0 [&>svg]:h-[17px] [&>svg]:w-[17px]', NOTE_TONE[tone].ico)}>{icon}</span>
    <span className={cn('flex min-w-0 flex-col gap-0.5', NOTE_TONE[tone].text)}>
      {title ? <span className="text-[14.5px] font-bold leading-snug">{title}</span> : null}
      {children ? <span className="text-[13.5px] leading-[1.4]">{children}</span> : null}
    </span>
  </div>
);

/** Il riquadro crema con un'icona e un testo (gli avvisi dentro le card). */
export const CreamNotice: React.FC<{ icon: React.ReactNode; children: React.ReactNode; className?: string }> = ({ icon, children, className }) => (
  <div className={cn(CREAM_BOX, 'flex items-start gap-2.5 px-3.5 py-3', className)}>
    <span aria-hidden="true" className={cn('mt-0.5 flex flex-shrink-0 [&>svg]:h-[17px] [&>svg]:w-[17px]', MUTED_TEXT)}>{icon}</span>
    <p className={cn('m-0 min-w-0 text-[14.5px] leading-[1.45]', INK_TEXT)}>{children}</p>
  </div>
);

// ---- Campo email ---------------------------------------------------------------------

interface EmailFieldProps {
  id: string;
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  /** Invio sulla tastiera. */
  onEnter?: () => void;
  placeholder?: string;
  error?: string | null;
}

/** Il campo dell'email della prenotazione: 48px, 16px (su iPhone la pagina non si ingrandisce). */
export const EmailField: React.FC<EmailFieldProps> = ({ id, label, value, onChange, onEnter, placeholder, error }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className={cn('text-[12.5px] font-bold', MUTED_TEXT)}>{label}</label>
    <span className="relative block">
      <Mail aria-hidden="true" className={cn('pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2', MUTED_TEXT)} strokeWidth={2} />
      <input
        id={id}
        type="email"
        inputMode="email"
        autoComplete="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) {
            e.preventDefault();
            onEnter();
          }
        }}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn(
          'min-h-[48px] w-full rounded-xl border bg-white pl-10 pr-3 text-base placeholder:text-stone-500 dark:bg-stone-950 dark:placeholder:text-stone-400',
          error ? 'border-red-600 dark:border-red-400' : 'border-nva-foot dark:border-stone-600',
          INK_TEXT,
          FOCUS_RING,
        )}
      />
    </span>
    {error ? (
      <p id={`${id}-error`} role="alert" className="m-0 text-[13px] leading-snug text-red-700 dark:text-red-300">{error}</p>
    ) : null}
  </div>
);
